import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Heart, FileText, BookOpen } from 'lucide-react';
import { useCart } from '../../context/CartContext';

const ProductCard = ({ product, layout = 'grid' }) => {
  const { addToCart, toggleWishlist, isWishlisted } = useCart();
  const wishlisted = isWishlisted(product.id);
  const isFree = product.price === 0;

  const detailUrl = `/marketplace/${product.slug}`;

  if (layout === 'list') {
    return (
      <Link to={detailUrl} className="flex items-center gap-6 py-4 border-b border-subtle last:border-b-0 hover:bg-subtle/50 transition-smooth">
        <div
          className="w-16 h-16 rounded-xl flex-shrink-0 flex items-center justify-center"
          style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)' }}
        >
          <img src={product.photo} alt={product.name} className="w-full h-full object-cover rounded-xl" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-sm font-semibold text-foreground-primary truncate">{product.name}</h3>
            <span
              className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
              style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316' }}
            >
              {product.badge}
            </span>
          </div>
          <p className="text-xs text-foreground-secondary truncate">{product.description}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-sm font-bold" style={{ color: isFree ? '#22c55e' : '#F97316' }}>
            {isFree ? 'Free' : `${product.currency}${product.price.toLocaleString()}`}
          </span>
          <div className="flex items-center gap-1" onClick={(e) => e.preventDefault()}>
            {product.datasheet && (
              <a href={product.datasheet} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-subtle transition-smooth text-foreground-muted" title="Datasheet" onClick={(e) => e.stopPropagation()}>
                <FileText size={14} />
              </a>
            )}
            {product.manual && (
              <a href={product.manual} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-subtle transition-smooth text-foreground-muted" title="Manual" onClick={(e) => e.stopPropagation()}>
                <BookOpen size={14} />
              </a>
            )}
            <button
              onClick={(e) => { e.preventDefault(); toggleWishlist(product); }}
              className="p-1.5 rounded-lg hover:bg-subtle transition-smooth"
              title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <Heart size={14} fill={wishlisted ? '#F97316' : 'none'} style={{ color: wishlisted ? '#F97316' : 'var(--fg-muted)' }} />
            </button>
            <button
              onClick={(e) => { e.preventDefault(); addToCart(product); }}
              className="p-1.5 rounded-lg transition-smooth"
              style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316' }}
              title="Add to cart"
            >
              <ShoppingCart size={14} />
            </button>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={detailUrl} className="glass-frosted rounded-2xl p-5 transition-smooth glow-subtle flex flex-col hover:brightness-110">
      <div
        className="w-full aspect-square rounded-xl mb-4 flex items-center justify-center overflow-hidden"
        style={{ background: 'rgba(249,115,22,0.04)', border: '1px solid rgba(249,115,22,0.1)' }}
      >
        <img src={product.photo} alt={product.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
      </div>

      <div className="flex items-center gap-2 mb-1">
        <span
          className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
          style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316' }}
        >
          {product.badge}
        </span>
        <span className="text-[10px] text-foreground-muted">{product.category}</span>
      </div>

      <h3 className="text-sm font-semibold text-foreground-primary mb-1 line-clamp-1">{product.name}</h3>
      <p className="text-xs text-foreground-secondary mb-3 line-clamp-2 flex-1">{product.description}</p>

      <div className="flex items-center justify-between mt-auto">
        <span className="text-base font-bold" style={{ color: isFree ? '#22c55e' : '#F97316' }}>
          {isFree ? 'Free' : `${product.currency}${product.price.toLocaleString()}`}
        </span>
        <div className="flex items-center gap-1" onClick={(e) => e.preventDefault()}>
          {product.datasheet && (
            <a href={product.datasheet} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-subtle transition-smooth text-foreground-muted" title="Datasheet" onClick={(e) => e.stopPropagation()}>
              <FileText size={14} />
            </a>
          )}
          <button
            onClick={(e) => { e.preventDefault(); toggleWishlist(product); }}
            className="p-1.5 rounded-lg hover:bg-subtle transition-smooth"
          >
            <Heart size={14} fill={wishlisted ? '#F97316' : 'none'} style={{ color: wishlisted ? '#F97316' : 'var(--fg-muted)' }} />
          </button>
          <button
            onClick={(e) => { e.preventDefault(); addToCart(product); }}
            className="p-1.5 rounded-lg transition-smooth"
            style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316' }}
          >
            <ShoppingCart size={14} />
          </button>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
